/**
 * SharedDigestFilterBar - Reusable digest profile filter bar
 * 
 * A unified horizontal chips bar for filtering content by digest profiles.
 * Used across: LitHub (Learn), LitStream (Audio), LitClub (Community), LitScholar
 * 
 * Features:
 * - Primary profile marked with star
 * - Unscreened count badge (optional)
 * - Last digest date
 * - % saved to LitHub (optional)
 * - "All" option at the end
 * - Click to select, visual highlight on active
 */
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Star, Layers, Clock, Percent, Loader2, Play, Plus } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import { preferencesAPI } from '../../api';
import { toast } from 'sonner';
import { LITPULSE_DIGEST_SLOT_ID } from '../../constants/litpulseChrome';
import { setPersistedDigestProfileId } from '../../utils/sharedDigestSelection';

export interface DigestProfileStat {
  profile_id: string;
  name: string;
  specialty_id: string;
  specialty_label: string;
  subspecialty_id: string;
  subspecialty_label: string;
  is_primary: boolean;
  last_digest_date: string | null;
  last_digest_id: string | null;
  unscreened_count: number;
  total_articles: number;
  saved_percent: number;
}

interface SharedDigestFilterBarProps {
  selectedProfileId: string | null;
  onSelectProfile: (profileId: string | null) => void;
  /** When provided, parent owns screen-stats fetching (avoids duplicate API calls). */
  profiles?: DigestProfileStat[];
  totalUnscreened?: number;
  statsLoading?: boolean;
  onRefreshStats?: () => Promise<void>;
  showUnscreenedBadge?: boolean;
  showSavedPercent?: boolean;
  showPrimaryStar?: boolean;
  showRunDigest?: boolean;
  onRunDigest?: (profileId: string) => void;
  runningProfileId?: string | null;
  centered?: boolean;
  allLabel?: string;
  allIcon?: React.ReactNode;
  /** When true, hide the "All Digests" chip / mobile option (LitScholar). */
  hideAllOption?: boolean;
  /** Persist selection to localStorage for cross-app continuity (default true). */
  persistSelection?: boolean;
  /** Max literature queues allowed on the user's plan (from listProfiles.max_profiles). */
  maxProfiles?: number;
  /** Show 1/5, 2/5 slot labels on each digest card. */
  showDigestSlots?: boolean;
  /** Route when "Add New Digest" is clicked. */
  addDigestPath?: string;
  showAddDigest?: boolean;
  className?: string;
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return 'No queues';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function getProfileLabel(profile: DigestProfileStat): string {
  return profile.name || (profile.subspecialty_label
    ? `${profile.specialty_label} - ${profile.subspecialty_label}`
    : profile.specialty_label);
}

const DIGEST_BAR_CLASS = 'bg-slate-50/95 backdrop-blur-sm border-b border-slate-200';

function useDigestChromeSlot() {
  const [slot, setSlot] = useState<HTMLElement | null>(() =>
    typeof document !== 'undefined'
      ? document.getElementById(LITPULSE_DIGEST_SLOT_ID)
      : null
  );

  useEffect(() => {
    const resolveSlot = () => document.getElementById(LITPULSE_DIGEST_SLOT_ID);

    const found = resolveSlot();
    if (found) {
      setSlot(found);
      return;
    }

    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      const el = resolveSlot();
      if (el) {
        setSlot(el);
        window.clearInterval(intervalId);
      } else if (attempts >= 20) {
        window.clearInterval(intervalId);
      }
    }, 50);

    return () => window.clearInterval(intervalId);
  }, []);

  return slot;
}

export const SharedDigestFilterBar: React.FC<SharedDigestFilterBarProps> = ({
  selectedProfileId,
  onSelectProfile,
  profiles: externalProfiles,
  totalUnscreened: externalTotalUnscreened,
  statsLoading: externalStatsLoading,
  onRefreshStats,
  showUnscreenedBadge = false,
  showSavedPercent = true,
  showPrimaryStar = true,
  showRunDigest = false,
  onRunDigest,
  runningProfileId = null,
  centered = false,
  allLabel = 'All Digests',
  allIcon,
  hideAllOption = false,
  persistSelection = true,
  maxProfiles: maxProfilesProp,
  showDigestSlots = true,
  addDigestPath = '/preferences',
  showAddDigest = true,
  className,
}) => {
  const navigate = useNavigate();
  const isControlled = externalProfiles !== undefined;
  const [internalProfiles, setInternalProfiles] = useState<DigestProfileStat[]>([]);
  const [internalTotalUnscreened, setInternalTotalUnscreened] = useState(0);
  const [internalMaxProfiles, setInternalMaxProfiles] = useState(1);
  const [internalLoading, setInternalLoading] = useState(!isControlled);
  const [error, setError] = useState<string | null>(null);
  const digestSlot = useDigestChromeSlot();

  const profiles = isControlled ? externalProfiles : internalProfiles;
  const totalUnscreened = isControlled ? (externalTotalUnscreened ?? 0) : internalTotalUnscreened;
  const planMaxProfiles = maxProfilesProp ?? internalMaxProfiles;
  const slotMax = Math.max(planMaxProfiles, profiles.length, 1);
  const loading = isControlled ? Boolean(externalStatsLoading) : internalLoading;
  const canAddDigest = showAddDigest && profiles.length < planMaxProfiles;

  const handleSelectProfile = useCallback(
    (profileId: string | null) => {
      if (persistSelection) {
        setPersistedDigestProfileId(profileId);
      }
      onSelectProfile(profileId);
    },
    [onSelectProfile, persistSelection]
  );

  const loadMaxProfiles = useCallback(async () => {
    if (maxProfilesProp !== undefined) return;
    try {
      const resp = await preferencesAPI.listProfiles();
      setInternalMaxProfiles(resp.max_profiles || 1);
    } catch {
      setInternalMaxProfiles(1);
    }
  }, [maxProfilesProp]);

  useEffect(() => {
    loadMaxProfiles();
  }, [loadMaxProfiles]);

  // Load profile stats (uncontrolled mode only)
  const loadProfileStats = useCallback(async () => {
    if (isControlled) {
      await onRefreshStats?.();
      return;
    }
    try {
      setInternalLoading(true);
      setError(null);
      const response = await preferencesAPI.getProfilesScreenStats();
      setInternalProfiles(response.profiles || []);
      setInternalTotalUnscreened(response.total_unscreened || 0);
      
      if (selectedProfileId === undefined && response.profiles?.length > 0) {
        const primary = response.profiles.find(p => p.is_primary);
        if (primary) {
          handleSelectProfile(primary.profile_id);
        }
      }
    } catch (err) {
      console.error('Failed to load profile stats:', err);
      setError('Failed to load profiles');
    } finally {
      setInternalLoading(false);
    }
  }, [isControlled, onRefreshStats, handleSelectProfile, selectedProfileId]);

  useEffect(() => {
    if (!isControlled) {
      loadProfileStats();
    }
  }, [isControlled, loadProfileStats]);

  // Handle set primary
  const handleSetPrimary = async (e: React.MouseEvent, profileId: string) => {
    e.stopPropagation();
    try {
      await preferencesAPI.setPrimaryProfile(profileId);
      toast.success('Primary queue updated');
      loadProfileStats();
    } catch {
      toast.error('Failed to set primary queue');
    }
  };

  if (loading) {
    const loadingBar = (
      <div className={cn(DIGEST_BAR_CLASS, 'flex items-center justify-center p-3', className)}>
        <Loader2 className="h-4 w-4 animate-spin text-slate-400 mr-2" />
        <span className="text-xs text-slate-500">Loading queues...</span>
      </div>
    );
    return digestSlot ? createPortal(loadingBar, digestSlot) : loadingBar;
  }

  if (error) {
    return null;
  }

  if (!loading && profiles.length === 0 && !canAddDigest) {
    return null;
  }

  const mobileSelectValue = selectedProfileId ?? (hideAllOption ? (profiles[0]?.profile_id ?? '') : '__all__');

  const mobileDropdown = (
    <div
      className={cn(DIGEST_BAR_CLASS, 'md:hidden flex flex-col gap-1.5 px-3 py-2', className)}
      data-testid="shared-digest-filter-mobile"
    >
      <p className="text-sm text-slate-600">Select your article digest.</p>
      <div className="flex items-center gap-2">
        <Select
          value={mobileSelectValue}
          onValueChange={(value) => handleSelectProfile(value === '__all__' ? null : value)}
        >
          <SelectTrigger className="h-9 flex-1 bg-white text-sm border-slate-200">
            <SelectValue placeholder={allLabel} />
          </SelectTrigger>
          <SelectContent>
            {!hideAllOption && (
            <SelectItem value="__all__">
              <span className="flex items-center gap-2">
                <Layers className="h-3.5 w-3.5 shrink-0" />
                <span>{allLabel}</span>
                {showUnscreenedBadge && totalUnscreened > 0 && (
                  <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
                    {totalUnscreened > 99 ? '99+' : totalUnscreened}
                  </span>
                )}
              </span>
            </SelectItem>
            )}
            {profiles.map((profile) => (
              <SelectItem key={profile.profile_id} value={profile.profile_id}>
                <span className="flex items-center gap-2">
                  {profile.is_primary && showPrimaryStar && (
                    <Star className="h-3 w-3 shrink-0 text-amber-500 fill-amber-500" />
                  )}
                  <span className="truncate">{getProfileLabel(profile)}</span>
                  {showUnscreenedBadge && profile.unscreened_count > 0 && (
                    <span className="ml-auto inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
                      {profile.unscreened_count > 99 ? '99+' : profile.unscreened_count}
                    </span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {showRunDigest && onRunDigest && selectedProfileId && (
          <button
            type="button"
            onClick={() => onRunDigest(selectedProfileId)}
            disabled={runningProfileId === selectedProfileId}
            title="Run Queue Now"
            className="shrink-0 flex h-9 w-9 items-center justify-center rounded-md border border-sky-200 bg-white text-sky-600 hover:bg-sky-50 disabled:opacity-50"
            data-testid={`run-digest-mobile-btn-${selectedProfileId}`}
          >
            {runningProfileId === selectedProfileId ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
    </div>
  );

  const bar = (
    <>
      {mobileDropdown}
      <div
        className={cn(
          DIGEST_BAR_CLASS,
          'hidden md:flex flex-nowrap gap-2 py-2 pl-3 pr-[min(22vw,7rem)] overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-thin',
          centered && 'md:justify-center',
          className
        )}
        data-testid="shared-digest-filter-bar"
      >
      {/* Individual Profile Chips */}
      {profiles.map((profile, index) => {
        const isSelected = selectedProfileId === profile.profile_id;
        const label = getProfileLabel(profile);

        return (
          <button
            key={profile.profile_id}
            onClick={() => handleSelectProfile(profile.profile_id)}
            className={cn(
              'group relative flex flex-col items-start px-2.5 py-1.5 rounded-lg border transition-all shrink-0 snap-start',
              'min-w-[128px] max-w-[180px]',
              isSelected
                ? 'bg-sky-50 border-sky-400 ring-2 ring-sky-200 shadow-sm'
                : 'bg-white border-slate-200 hover:border-sky-300 hover:bg-sky-50/50'
            )}
            data-testid={`digest-filter-chip-${profile.profile_id}`}
          >
            {/* Header row: slot + label + badges + actions */}
            <div className="flex items-center justify-between w-full gap-1">
              <div className="flex items-center gap-1 min-w-0 flex-1">
                {showDigestSlots && (
                  <span
                    className={cn(
                      'text-[10px] font-semibold tabular-nums px-1 py-0.5 rounded shrink-0',
                      isSelected ? 'bg-sky-200 text-sky-800' : 'bg-slate-100 text-slate-600'
                    )}
                    data-testid={`digest-slot-${profile.profile_id}`}
                  >
                    {index + 1}/{slotMax}
                  </span>
                )}
                <span className={cn(
                  'relative inline-block text-xs font-medium truncate pr-3 max-w-full',
                  isSelected ? 'text-sky-700' : 'text-slate-700'
                )}>
                  {label}
                  {showUnscreenedBadge && profile.unscreened_count > 0 && (
                    <span
                      className={cn(
                        'absolute -top-1.5 right-0 flex h-4 min-w-4 items-center justify-center rounded-full px-0.5 text-[9px] font-bold leading-none',
                        isSelected ? 'bg-sky-600 text-white' : 'bg-rose-500 text-white'
                      )}
                    >
                      {profile.unscreened_count > 99 ? '99+' : profile.unscreened_count}
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                {showRunDigest && onRunDigest && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRunDigest(profile.profile_id); }}
                    disabled={runningProfileId === profile.profile_id}
                    title="Run Queue Now"
                    className="p-0.5 rounded text-sky-600 hover:bg-sky-100 disabled:opacity-50"
                    data-testid={`run-digest-btn-${profile.profile_id}`}
                  >
                    {runningProfileId === profile.profile_id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Play className="h-3 w-3" />
                    )}
                  </button>
                )}
                {showPrimaryStar && (
                  profile.is_primary ? (
                    <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />
                  ) : (
                    <span
                      onClick={(e) => handleSetPrimary(e, profile.profile_id)}
                      title="Set as primary"
                      className="shrink-0"
                    >
                      <Star
                        className="h-3 w-3 text-slate-300 opacity-0 group-hover:opacity-100 hover:text-amber-400 cursor-pointer transition-opacity"
                      />
                    </span>
                  )
                )}
              </div>
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
              <span className="flex items-center gap-0.5">
                <Clock className="h-2.5 w-2.5" />
                {formatDate(profile.last_digest_date)}
              </span>
              
              {showSavedPercent && (
                <span className="flex items-center gap-0.5">
                  <Percent className="h-2.5 w-2.5" />
                  {profile.saved_percent}%
                </span>
              )}
            </div>
          </button>
        );
      })}

      {/* All Digests */}
      {!hideAllOption && profiles.length > 0 && (
        <button
          onClick={() => handleSelectProfile(null)}
          className={cn(
            'relative flex flex-col items-start px-2.5 py-1.5 rounded-lg border transition-all shrink-0 snap-start',
            'min-w-[112px]',
            selectedProfileId === null
              ? 'bg-violet-50 border-violet-400 ring-2 ring-violet-200 shadow-sm'
              : 'bg-white border-slate-200 hover:border-violet-300 hover:bg-violet-50/50'
          )}
          data-testid="digest-filter-chip-all"
        >
          <div className="flex items-center gap-1.5 min-w-0">
            {allIcon || (
              <Layers className={cn(
                'h-3.5 w-3.5 shrink-0',
                selectedProfileId === null ? 'text-violet-600' : 'text-slate-500'
              )} />
            )}
            <span className={cn(
              'relative inline-block text-xs font-medium pr-3',
              selectedProfileId === null ? 'text-violet-700' : 'text-slate-700'
            )}>
              {allLabel}
              {showUnscreenedBadge && totalUnscreened > 0 && (
                <span
                  className={cn(
                    'absolute -top-1.5 right-0 flex h-4 min-w-4 items-center justify-center rounded-full px-0.5 text-[9px] font-bold leading-none',
                    selectedProfileId === null ? 'bg-violet-600 text-white' : 'bg-rose-500 text-white'
                  )}
                >
                  {totalUnscreened > 99 ? '99+' : totalUnscreened}
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
            <span>{profiles.length} digest{profiles.length !== 1 ? 's' : ''}</span>
          </div>
        </button>
      )}

      {/* Add New Digest */}
      {canAddDigest && (
        <button
          type="button"
          onClick={() => navigate(addDigestPath)}
          className={cn(
            'flex flex-col items-center justify-center px-2.5 py-1.5 rounded-lg border-2 border-dashed transition-all shrink-0 snap-start',
            'min-w-[128px] max-w-[160px]',
            'border-sky-300 bg-sky-50/40 hover:bg-sky-50 hover:border-sky-400'
          )}
          data-testid="digest-filter-add-new"
        >
          <Plus className="h-4 w-4 text-sky-600 mb-0.5" />
          <span className="text-xs font-semibold text-sky-700 text-center leading-tight">
            Add New Digest
          </span>
          <span className="text-[10px] text-sky-600/80 mt-0.5 tabular-nums">
            {profiles.length}/{planMaxProfiles} used
          </span>
        </button>
      )}
    </div>
    </>
  );

  return digestSlot ? createPortal(bar, digestSlot) : bar;
};

// Hook to use the digest filter state
export const useDigestFilter = () => {
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<DigestProfileStat[]>([]);
  const [loading, setLoading] = useState(true);

  const loadProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await preferencesAPI.getProfilesScreenStats();
      setProfiles(response.profiles || []);
      
      // Auto-select primary if none selected
      if (!selectedProfileId && response.profiles?.length > 0) {
        const primary = response.profiles.find(p => p.is_primary);
        if (primary) {
          setSelectedProfileId(primary.profile_id);
        }
      }
    } catch (err) {
      console.error('Failed to load profiles:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedProfileId]);

  useEffect(() => {
    loadProfiles();
  }, []);

  return {
    selectedProfileId,
    setSelectedProfileId,
    profiles,
    loading,
    selectedProfile: profiles.find(p => p.profile_id === selectedProfileId),
  };
};

export default SharedDigestFilterBar;
